import React from 'react';
import PageHero from '../components/PageHero';
import CtaSection from '../components/CtaSection';
import TechDocLayout, { DocSection } from '../components/TechDocLayout';
import { Statement } from '../components/Statement';
import { RuledSpread } from '../components/System';
import useSeo from '../lib/useSeo';

const PRINCIPLES = [
  { title: 'Kept apart', detail: 'Each firm and each client sees only its own data.' },
  { title: 'Passwords encrypted', detail: 'Database passwords are encrypted on entry and never shown again.' },
  { title: 'Data stays put', detail: 'The maths runs in the client’s database. Only totals come back.' },
  { title: 'Numbers you can trace', detail: 'The AI never does the arithmetic. Every figure has a record.' },
];

const LIFECYCLE = [
  { step: 'Sign in', detail: 'You prove who you are.' },
  { step: 'Verify', detail: 'We check the sign-in is real and current.' },
  { step: 'Match', detail: 'We link you to your firm.' },
  { step: 'Scope', detail: 'Every query is limited to your firm.' },
  { step: 'Client check', detail: 'Unknown or foreign clients are rejected.' },
];

const IN_PLACE = [
  'Firm-level access control on all data and chat.',
  'Encrypted database passwords.',
  'Sign-in checks on every request.',
  'A full record behind every answer.',
  'Clients kept apart inside one firm. Owner-approved invites for teammates.',
];

const AHEAD = [
  'Stronger sign-in verification in production.',
  'Moving from sample data to real client workloads.',
  'An independent security review.',
  'Per-client permissions. Today, teammates see all of a firm’s clients.',
];

const SECTIONS = [
  { id: 'in-short', label: 'In short' },
  { id: 'principles', label: 'Core principles' },
  { id: 'authentication', label: 'Authentication' },
  { id: 'status', label: 'Where we are' },
];

function StatusList({ title, items, accent }) {
  return (
    <div className={`border-t-2 pt-4 ${accent ? 'border-bronze' : 'border-wall/25'}`}>
      <h3 className={`label mb-3 font-sans ${accent ? 'text-bronze-deep' : 'text-glass'}`}>{title}</h3>
      <ul className="m-0 list-none p-0">
        {items.map((item) => (
          <li key={item} className="border-t border-stone py-3">{item}</li>
        ))}
      </ul>
    </div>
  );
}

function Security() {
  useSeo({
    title: 'Security & Privacy',
    path: '/security',
    description: 'How Sidwell protects client data: it stays in the client’s own database, passwords are encrypted, and every client is walled off from every other.',
  });

  return (
    <main className="relative w-full">
      <PageHero
        variant="memo"
        image="between"
        eyebrow="Security & Privacy"
        title="Your data. Locked down."
        subtitle="How we protect your clients’ data, in plain words."
        badge="Verified sign-in"
      />

      <TechDocLayout sections={SECTIONS}>
        <DocSection id="in-short">
          <Statement title="In plain terms">
            <p className="border-t border-wall/70 px-5 py-7 font-display text-[clamp(24px,2.6vw,38px)] font-light leading-[1.2] sm:px-7">
              Data stays in the client&rsquo;s database. Only totals come back. Passwords are encrypted. Everyone is walled off.
            </p>
          </Statement>
        </DocSection>

        <DocSection id="principles" label="Core principles" title="What we guarantee." intro="Four promises behind every answer.">
          <RuledSpread items={PRINCIPLES} cols={2} notes />
        </DocSection>

        <DocSection
          id="authentication"
          label="Authentication"
          title="Sign-in you can trust."
          intro="Every request carries a verified sign-in. A missing or invalid one is rejected."
        >
          <Statement title="Every request, in order" shadow={false}>
            <ol className="m-0 list-none border-t border-wall/70 p-0">
              {LIFECYCLE.map((row, i) => (
                <li key={row.step} className="grid grid-cols-[28px_1fr] items-baseline gap-x-3 border-t border-stone px-5 py-4 first:border-t-0 sm:grid-cols-[28px_200px_1fr] sm:px-7">
                  <span className="text-[14px] text-glass figure">{i + 1}</span>
                  <span className="font-semibold">{row.step}</span>
                  <span className="col-start-2 text-glass sm:col-start-3">{row.detail}</span>
                </li>
              ))}
            </ol>
          </Statement>
        </DocSection>

        <DocSection id="status" label="Where we are" title="Honest status." intro="Sidwell is an early product. Here is where security stands.">
          <div className="grid grid-cols-1 gap-x-10 gap-y-10 md:grid-cols-2">
            <StatusList title="In place" items={IN_PLACE} accent />
            <StatusList title="Hardening ahead" items={AHEAD} />
          </div>
        </DocSection>
      </TechDocLayout>

      <CtaSection
        image="entrance"
        eyebrow="Talk to Us"
        title="Security questions?"
        subtitle="We’ll walk you through it."
        primary={{ label: 'Request a demo', to: '/#request-briefing' }}
        secondary={{ label: 'How it’s built', to: '/innovation' }}
      />
    </main>
  );
}

export default Security;
